/**
 * New job — a field tech creates the Spire work order themselves.
 *
 * For clients whose techs get work from an outside dispatcher ("Enercare
 * mode", Admin → Field jobs). The tech keys the dispatch details in and
 * the order is born in Spire assigned to them for the chosen date/time,
 * so the normal on-site flow (start → parts → photos → notes → sign →
 * clock-out) attaches everything to it and the office sees it on the
 * board at once.
 *
 * Bill-to defaults to the configured account (e.g. Enercare) but the tech
 * may pick any customer; the homeowner's name + service address go in the
 * order's ship-to — which is what every job-site surface reads.
 *
 * Offline: the order goes through the same outbox as quotes. It shows on
 * My Day as "queued" until the POST lands.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Briefcase, Loader2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { CustomerPicker } from '../components/customer-picker.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Feedback } from '../components/ui/feedback.js';
import { Input } from '../components/ui/input.js';
import { api, type CreateSalesOrderBody } from '../lib/api.js';
import {
  BLOCK_HOURS,
  assignmentUdfPatch,
  compactHourLabel,
  snapToBlock,
  todayLocalIso,
} from '../lib/dispatch-board.js';
import { enqueueSalesOrder, generateEntryId } from '../lib/outbox.js';
import { drainOutbox } from '../lib/outbox-processor.js';
import { useFieldJobsSettings, useSchedulingSettings } from '../lib/settings.js';
import { activeSalesTypes, useSalesTypes } from '../lib/trade.js';

export const FIELD_NOTE_SUBJECT = 'Tinsmith field note';

export function NewJobScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fieldJobs = useFieldJobsSettings();
  const scheduling = useSchedulingSettings();
  const salesTypes = useSalesTypes();
  const types = activeSalesTypes(salesTypes);
  const me = useQuery({ queryKey: ['me'], queryFn: ({ signal }) => api.me.get(signal), staleTime: 5 * 60_000 });
  const techId = me.data?.techId ?? null;

  const today = todayLocalIso();
  const [customerNo, setCustomerNo] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [homeowner, setHomeowner] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [prov, setProv] = useState('ON');
  const [postal, setPostal] = useState('');
  const [phone, setPhone] = useState('');
  const [po, setPo] = useState('');
  const [reference, setReference] = useState('');
  const [typeKey, setTypeKey] = useState('');
  const [date, setDate] = useState(today);
  const [startHour, setStartHour] = useState<number>(() => nextBlock(scheduling.dayStartHour));
  const [duration, setDuration] = useState<number>(scheduling.defaultJobHours);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Bill-to defaults to the configured account once settings load; the
  // tech can still change it (extras billed to the homeowner, etc.).
  const billToNo = customerNo || fieldJobs.billToCustomerNo || '';
  const billToLabel = customerNo ? customerName : fieldJobs.billToCustomerName ?? fieldJobs.billToCustomerNo ?? '';

  const startOptions = useMemo(() => {
    const out: number[] = [];
    for (let h = scheduling.dayStartHour; h < scheduling.dayEndHour; h += BLOCK_HOURS) out.push(h);
    if (!out.includes(startHour)) out.push(startHour);
    return out.sort((a, b) => a - b);
  }, [scheduling.dayStartHour, scheduling.dayEndHour, startHour]);
  const durationOptions = useMemo(() => {
    const out: number[] = [];
    for (let d = BLOCK_HOURS; d <= 12; d += BLOCK_HOURS) out.push(d);
    return out;
  }, []);

  const problems: string[] = [];
  if (!billToNo) problems.push('bill-to customer');
  if (!homeowner.trim()) problems.push('homeowner name');
  if (!line1.trim()) problems.push('street address');
  if (fieldJobs.requirePo && !po.trim()) problems.push('PO number');
  if (!typeKey) problems.push('job type');
  const canSubmit = problems.length === 0 && !!techId && !submitting;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !techId) return;
    setSubmitting(true);
    setError(null);
    const typeLabel = types.find((t) => t.key === typeKey)?.label ?? typeKey;
    const body: CreateSalesOrderBody = {
      customerNo: billToNo,
      type: 'W',
      hold: false,
      orderDate: today,
      requiredDate: date,
      ...(po.trim() ? { customerPO: po.trim().slice(0, 40) } : {}),
      ...(reference.trim() ? { referenceNo: reference.trim().slice(0, 40) } : {}),
      shippingAddress: {
        name: homeowner.trim().slice(0, 60),
        line1: line1.trim().slice(0, 60),
        ...(line2.trim() ? { line2: line2.trim().slice(0, 60) } : {}),
        ...(city.trim() ? { city: city.trim().slice(0, 40) } : {}),
        ...(prov.trim() ? { provState: prov.trim().toUpperCase().slice(0, 10) } : {}),
        ...(postal.trim() ? { postalCode: postal.trim().toUpperCase().slice(0, 12) } : {}),
        ...(phone.trim() ? { phone: phone.trim().slice(0, 25) } : {}),
      },
      udf: {
        ...assignmentUdfPatch({ techId, date, startHour, duration: snapToBlock(duration) }, { resetPhase: true }),
        [fieldJobs.typeUdfKey]: typeLabel,
      },
      items: [],
    };
    try {
      await enqueueSalesOrder({
        entryId: generateEntryId(),
        idempotencyKey: crypto.randomUUID(),
        body,
        note: note.trim() || null,
        noteSubject: FIELD_NOTE_SUBJECT,
      });
      void drainOutbox().then(() => {
        void queryClient.invalidateQueries({ queryKey: ['field', 'day-list', techId] });
        void queryClient.invalidateQueries({ queryKey: ['operations'] });
      });
      void queryClient.invalidateQueries({ queryKey: ['field', 'day-list', techId] });
      void navigate({ to: '/jobs' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not queue the job.');
      setSubmitting(false);
    }
  };

  if (!fieldJobs.enabled) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Feedback variant="info" size="lg">
          Creating jobs from the field is turned off for this company. Ask the office to enable it under Admin → Field jobs.
        </Feedback>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackLink />
      <header>
        <div className="text-xs text-ink-muted inline-flex items-center gap-1.5">
          <Briefcase className="h-3.5 w-3.5" aria-hidden /> Field-created job
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">New job</h1>
        <p className="text-xs text-ink-muted mt-1">
          Creates the work order in Spire and puts it on your day. Add parts, photos and the signature on site as usual.
        </p>
      </header>

      {me.isSuccess && !techId && (
        <Feedback variant="warning" size="lg">
          Your account isn't linked to a tech on the dispatch roster yet. Ask an admin to set this on your user (Admin → Users → Dispatch tech link).
        </Feedback>
      )}

      <form onSubmit={submit} className="space-y-3">
        <Card>
          <CardHeader><CardTitle>Bill to</CardTitle></CardHeader>
          <CardBody className="space-y-2">
            <CustomerPicker
              value={billToNo}
              valueLabel={billToLabel || undefined}
              onSelect={(c) => {
                setCustomerNo(c.customerNo);
                setCustomerName(c.name);
              }}
            />
            {!customerNo && fieldJobs.billToCustomerNo && (
              <div className="text-[11px] text-ink-subtle">
                Defaults to {fieldJobs.billToCustomerName ?? fieldJobs.billToCustomerNo}. Search above to bill someone else (e.g. the homeowner for extras).
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>Service address (ship-to)</CardTitle></CardHeader>
          <CardBody className="space-y-2">
            <Field label="Homeowner / site name" required>
              <Input value={homeowner} onChange={(e) => setHomeowner(e.target.value)} maxLength={60} autoComplete="off" />
            </Field>
            <Field label="Street" required>
              <Input value={line1} onChange={(e) => setLine1(e.target.value)} maxLength={60} autoComplete="street-address" />
            </Field>
            <Field label="Unit / line 2">
              <Input value={line2} onChange={(e) => setLine2(e.target.value)} maxLength={60} />
            </Field>
            <div className="grid grid-cols-[2fr_1fr_1.4fr] gap-2">
              <Field label="City"><Input value={city} onChange={(e) => setCity(e.target.value)} maxLength={40} /></Field>
              <Field label="Prov"><Input value={prov} onChange={(e) => setProv(e.target.value)} maxLength={10} className="uppercase" /></Field>
              <Field label="Postal"><Input value={postal} onChange={(e) => setPostal(e.target.value)} maxLength={12} className="uppercase" autoComplete="postal-code" /></Field>
            </div>
            <Field label="Phone">
              <Input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={25} autoComplete="tel" />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>Dispatch details</CardTitle></CardHeader>
          <CardBody className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="PO number" required={fieldJobs.requirePo}>
                <Input value={po} onChange={(e) => setPo(e.target.value)} maxLength={40} className="font-mono" autoComplete="off" />
              </Field>
              <Field label="Reference #">
                <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={40} className="font-mono" autoComplete="off" />
              </Field>
            </div>
            <Field label="Job type" required>
              <select
                value={typeKey}
                onChange={(e) => setTypeKey(e.target.value)}
                className="h-11 w-full rounded-lg border bg-surface px-3 text-base"
                style={{ borderColor: 'var(--color-border-secondary)' }}
              >
                <option value="">Choose…</option>
                {types.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2">
              <Field label="Date" required>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              </Field>
              <Field label="Start">
                <select value={startHour} onChange={(e) => setStartHour(Number(e.target.value))} className="h-11 w-full rounded-lg border bg-surface px-2 text-base" style={{ borderColor: 'var(--color-border-secondary)' }}>
                  {startOptions.map((h) => (
                    <option key={h} value={h}>{compactHourLabel(h)}</option>
                  ))}
                </select>
              </Field>
              <Field label="Length">
                <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="h-11 w-full rounded-lg border bg-surface px-2 text-base" style={{ borderColor: 'var(--color-border-secondary)' }}>
                  {durationOptions.map((d) => (
                    <option key={d} value={d}>{d} h</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Note for the order (optional)">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="What Enercare asked for, access instructions, anything the office should know."
                className="block w-full rounded-md border bg-surface px-3 py-2 text-sm text-ink"
                style={{ borderColor: 'var(--color-border-secondary)' }}
              />
            </Field>
          </CardBody>
        </Card>

        {error && <Feedback variant="error" size="sm">{error}</Feedback>}
        {problems.length > 0 && (
          <div className="text-[11px] text-ink-subtle">Still needed: {problems.join(', ')}.</div>
        )}
        <Button type="submit" disabled={!canSubmit} className="w-full h-12">
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" aria-hidden /> Creating…</> : 'Create job & add to my day'}
        </Button>
        <p className="text-[11px] text-ink-subtle">
          Works offline — the order is created in Spire as soon as you have a signal.
        </p>
      </form>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink">
      <ArrowLeft className="h-4 w-4" aria-hidden />
      My day
    </Link>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-sm space-y-1">
      <span className="text-xs uppercase tracking-wider text-ink-subtle">
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

/** Next half-hour block from now, clamped to the company day start. */
function nextBlock(dayStartHour: number): number {
  const now = new Date();
  const h = now.getHours() + now.getMinutes() / 60;
  return Math.max(dayStartHour, snapToBlock(Math.ceil(h / BLOCK_HOURS) * BLOCK_HOURS));
}
